import React, { useState, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, isValid, parseISO } from 'date-fns';

// ─── Types ──────────────────────────────────────────────────────────────────
interface StudiesDateRangePickerProps {
    startDate: string;      // "YYYY-MM-DD" or ""
    endDate: string;        // "YYYY-MM-DD" or ""
    onStartChange: (val: string) => void;
    onEndChange: (val: string) => void;
    onClear: () => void;
}

// ─── Helper ─────────────────────────────────────────────────────────────────
function toDate(str: string): Date | null {
    if (!str) return null;
    const d = parseISO(str);
    return isValid(d) ? d : null;
}

function toYMD(d: Date | null): string {
    if (!d || !isValid(d)) return '';
    return format(d, 'yyyy-MM-dd');
}

// ─── Inline styles injected once ────────────────────────────────────────────
const PICKER_STYLES = `
.sdp-wrapper .react-datepicker-wrapper,
.sdp-wrapper .react-datepicker__input-container {
    display: block;
    width: 100%;
    height: 100%;
}
.sdp-wrapper input {
    width: 100%;
    height: 100%;
    background: transparent;
    border: none;
    outline: none;
    font-size: 13px;
    font-weight: 500;
    color: inherit;
    cursor: pointer;
    padding: 0;
}
.sdp-wrapper input::placeholder {
    color: var(--text-muted, #9ca3af);
}
/* Calendar popup */
.sdp-cal.react-datepicker {
    font-family: inherit;
    border: 1px solid var(--border-subtle, #e2e8f0);
    border-radius: 10px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.12);
    background: var(--bg-elevated, #fff);
    overflow: hidden;
}
.sdp-cal .react-datepicker__header {
    background: var(--v2-navy-600, #1e3a5f);
    border-bottom: none;
    padding-top: 12px;
}
.sdp-cal .react-datepicker__current-month,
.sdp-cal .react-datepicker__day-name {
    color: #fff;
    font-weight: 600;
}
.sdp-cal .react-datepicker__navigation-icon::before {
    border-color: rgba(255,255,255,0.8);
}
.sdp-cal .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
    border-color: #fff;
}
.sdp-cal .react-datepicker__day {
    color: var(--text-primary, #1a202c);
    border-radius: 6px;
    font-size: 13px;
    transition: background 0.15s;
}
.sdp-cal .react-datepicker__day:hover {
    background: var(--v2-teal-100, #e0f2fe);
    color: var(--v2-navy-700, #1e3a5f);
}
.sdp-cal .react-datepicker__day--selected,
.sdp-cal .react-datepicker__day--range-start,
.sdp-cal .react-datepicker__day--range-end {
    background: var(--v2-navy-600, #1e3a5f) !important;
    color: #fff !important;
    font-weight: 700;
}
.sdp-cal .react-datepicker__day--in-range {
    background: var(--v2-teal-100, #e0f2fe);
    color: var(--v2-navy-700, #1e3a5f);
}
.sdp-cal .react-datepicker__day--in-selecting-range {
    background: var(--v2-teal-200, #bae6fd);
}
.sdp-cal .react-datepicker__day--outside-month {
    opacity: 0.35;
}
.sdp-cal .react-datepicker__day--disabled {
    opacity: 0.3;
    cursor: not-allowed;
}
.sdp-cal .react-datepicker__day--keyboard-selected {
    background: var(--v2-teal-100, #e0f2fe);
}
`;

let stylesInjected = false;
function ensureStyles() {
    if (stylesInjected) return;
    const el = document.createElement('style');
    el.textContent = PICKER_STYLES;
    document.head.appendChild(el);
    stylesInjected = true;
}

// ─── Calendar icon SVG ───────────────────────────────────────────────────────
const CalIcon = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

const XIcon = ({ size = 12, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

// ─── Single styled date input ────────────────────────────────────────────────
interface StyledDateInputProps {
    value: string;
    onChange: (val: string) => void;
    placeholder: string;
    maxDate?: Date | null;
    minDate?: Date | null;
}

const StyledDateInput: React.FC<StyledDateInputProps> = ({ value, onChange, placeholder, maxDate, minDate }) => {
    ensureStyles();
    const selected = toDate(value);

    return (
        <div
            className="sdp-wrapper flex h-full min-w-0 flex-1 items-center gap-2 rounded-[var(--radius-md)] border px-3 transition-all focus-within:ring-2"
            style={{
                background: 'var(--bg-card)',
                borderColor: 'var(--border-subtle)',
                color: value ? 'var(--text-primary)' : 'var(--text-muted)',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)',
            }}
        >
            <CalIcon size={14} color="var(--text-muted)" />
            <DatePicker
                selected={selected}
                onChange={(date: Date | null) => onChange(toYMD(date))}
                placeholderText={placeholder}
                dateFormat="dd MMM yyyy"
                maxDate={maxDate ?? undefined}
                minDate={minDate ?? undefined}
                calendarClassName="sdp-cal"
                showPopperArrow={false}
                popperPlacement="bottom-start"
                popperModifiers={[
                    { name: 'offset', options: { offset: [0, 6] } } as any,
                ]}
                isClearable={false}
                autoComplete="off"
            />
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const StudiesDateRangePicker: React.FC<StudiesDateRangePickerProps> = ({
    startDate,
    endDate,
    onStartChange,
    onEndChange,
    onClear,
}) => {
    const startDateObj = toDate(startDate);
    const endDateObj = toDate(endDate);
    const hasValue = !!(startDate || endDate);

    return (
        <div className="flex h-10 items-center gap-1.5">
            {/* Start Date */}
            <StyledDateInput
                value={startDate}
                onChange={onStartChange}
                placeholder="Start date"
                maxDate={endDateObj}
            />

            {/* Separator */}
            <span
                className="flex-shrink-0 select-none text-[11px] font-bold"
                style={{ color: 'var(--text-muted)' }}
            >
                →
            </span>

            {/* End Date */}
            <StyledDateInput
                value={endDate}
                onChange={onEndChange}
                placeholder="End date"
                minDate={startDateObj}
            />

            {/* Clear button */}
            {hasValue && (
                <button
                    onClick={onClear}
                    title="Clear date filter"
                    className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition-colors"
                    style={{
                        background: 'var(--v2-danger-500, #ef4444)',
                        color: '#fff',
                    }}
                >
                    <XIcon size={12} />
                </button>
            )}
        </div>
    );
};

export default StudiesDateRangePicker;
